import { Component } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { Department } from "@enums/department.enum";
import { Machine } from "@interfaces/models/machine.interface";
import { ToolroomProcess } from "@interfaces/models/toolroomProcess.interface";
import { ToolroomProcessMachine } from "@interfaces/models/toolroomProcessMachine.interface";
import { MachineService } from "@services/machine.service";
import { SystemParameterService } from "@services/systemParameter.service";
import { ToolroomProcessService } from "@services/toolroomProcess.service";
import { ToolroomProcessMachineService } from "@services/toolroomProcessMachine.service";
import { ToastService } from "angular-toastify";
import { ToastApiError } from "@helpers/apiError";

@Component({
	moduleId: module.id,
	selector: "toolroomProcesses-id",
	styleUrls: ["toolroomProcesses-id.css"],
	templateUrl: "toolroomProcesses-id.html"
})
export class ToolroomProcessesIdAdminComponent {
	activeMachineIds: number[] = [];
	createEnabled = true;
	editEnabled = true;
	headerPrimaryButtons: AdminHeaderButton[] = [];
	item: ToolroomProcess = new ToolroomProcess();
	itemType = "Toolroom Process";
	machines: Machine[] = [];
	readOnly = true;
	returnUrl: string = location.href.split("/")[3] + "/" + location.href.split("/")[4];
	title: string = "View " + this.itemType;

	constructor(private router: Router, private toastService: ToastService, private route: ActivatedRoute, private itemService: ToolroomProcessService, private machineService: MachineService, private systemParameterService: SystemParameterService, private toolroomProcessMachineService: ToolroomProcessMachineService) {
		this.buildButtons("view");
		this.getMachines();
		this.route.params
			.subscribe((params) => {
				if (params.id != "create") {
					if (params.id.split("_").length > 1) {
						this.getItem(params.id.split("_")[0]);
						this.edit();
					} else {
						this.getItem(params.id);
					}
				} else {
					this.create();
				}
			});
	}

	buildButtons(mode: string) {
		if (mode == "edit" && this.editEnabled) {
			this.headerPrimaryButtons = [
				{ method: "updateItem", text: "Save Changes" },
				{ method: "cancelEditing", text: "View Mode" },
				{ method: "close", text: "Close" }
			];
		} else if (mode == "create" && this.createEnabled) {
			this.headerPrimaryButtons = [
				{ method: "createItem", text: "Save" },
				{ method: "close", text: "Close" }
			];
		} else if (mode == "view") {
			this.headerPrimaryButtons = [
				{ method: "edit", text: "Edit" },
				{ method: "close", text: "Close" }
			];
		}
	}

	cancelEditing() {
		if (confirm("Are you sure you wish to switch modes? Unsaved changes will be discarded.") == false) {
			return;
		}
		this.readOnly = true;
		this.buildButtons("view");
		this.title = "View " + this.itemType;
	}

	close() {
		this.router.navigate([`/${this.returnUrl}/`]);
	}

	create() {
		this.readOnly = false;
		this.buildButtons("create");
		this.title = "Create New " + this.itemType;
	}

	createItem() {
		if (!this.isValid()) {
			return;
		}
		this.itemService.createSingle(this.item)
			.subscribe(
				(res: any) => {
					this.saveMachines(res.response.lookupId);
					this.toastService.success("Successfully Created " + this.itemType);
					this.close();
				},
				(err) => { console.log(err); ToastApiError(this.toastService, err, "Something Went Wrong!"); }
			);
	}

	edit() {
		this.readOnly = false;
		this.buildButtons("edit");
		this.title = "Edit " + this.itemType;
		this.toastService.warn("You are now editing...");
	}

	getItem(itemId: number) {
		this.itemService.getSingleById(itemId)
			.subscribe(
				(res: any) => {
					this.item = res.response;
					this.getMachineLinks();
				},
				(err) => { console.log(err); ToastApiError(this.toastService, err); }
			);
	}

	getMachineLinks() {
		this.toolroomProcessMachineService.forToolroomProcess(this.item.id)
			.subscribe(
				(res: any) => this.activeMachineIds = (res.response as ToolroomProcessMachine[]).map((link) => link.machineId),
				(err) => console.log(err)
			);
	}

	// Toolroom processes run on toolroom machines, so offer the same list as the program sheet's Machine
	// Centre: machines at the toolroom site, less back office kit. Without the setting, every machine.
	getMachines() {
		this.machineService.getAll()
			.subscribe(
				(res: any) => {
					const all = (res.response.items as Machine[]).sort((a, b) => (a.cellNumber || "").localeCompare(b.cellNumber || ""));
					this.machines = all;
					this.systemParameterService.getValueByName("ToolroomSettings").subscribe(
						(value: any) => {
							const siteId = value?.ToolroomSiteId;
							if (siteId) {
								this.machines = all.filter((m) => m.siteId === siteId && m.department !== Department.BackOffice);
							}
						},
						(err) => console.log("ToolroomSettings SystemParam not configured:", err)
					);
				},
				(err) => console.log(err)
			);
	}

	headerButtonClick(method: any) {
		// @ts-ignore // Required to be able to call the method directly from the variable
		if (this[method]) this[method]();
	}

	// Mirrors the API's rules so the user hears about them before saving: a default process has to say
	// where it goes, and OP numbers go up in tens.
	isValid(): boolean {
		if (!(this.item.name || "").trim()) {
			this.toastService.error("Give the process a name");
			return false;
		}
		if (this.item.isDefault && !this.item.defaultOpNumber) {
			this.toastService.error("A default process needs a default OP number");
			return false;
		}
		if (this.item.defaultOpNumber && this.item.defaultOpNumber % 10 !== 0) {
			this.toastService.error("OP numbers go up in tens - 10, 20, 30");
			return false;
		}
		return true;
	}

	saveMachines(lookupId: any) {
		const linked = this.machines.filter((m) => this.activeMachineIds.indexOf(m.id) !== -1);
		this.toolroomProcessMachineService.updateMachinesForToolroomProcess(lookupId, linked)
			.subscribe(
				() => console.log("Machines saved"),
				(err) => { console.log(err); ToastApiError(this.toastService, err, "The machines could not be saved"); }
			);
	}

	toggleMachine(machine: Machine) {
		if (this.readOnly) return;
		const index = this.activeMachineIds.indexOf(machine.id);
		if (index === -1) this.activeMachineIds.push(machine.id);
		else this.activeMachineIds.splice(index, 1);
	}

	updateItem() {
		if (!this.isValid()) {
			return;
		}
		this.itemService.updateSingleById(this.item.id, this.item)
			.subscribe(
				() => {
					this.saveMachines(this.item.lookupId);
					this.toastService.success("Successfully Updated");
					this.readOnly = true;
					this.buildButtons("view");
					this.title = "View " + this.itemType;
				},
				(err) => { console.log(err); ToastApiError(this.toastService, err, "Something Went Wrong!"); }
			);
	}
}
